import type {
  OutcomeDefinition,
  PlaybookTree,
} from "@repo/app-types/entities/playbook";
import { getPlaybookNode } from "@repo/app-types/entities/playbook";
import {
  PlaybookTestResultStatus,
  type AdminApiPlaybookTestResult,
  type AdminApiPlaybookTestSpec,
  type AdminApiPlaybookTestSpecResult,
  type PlaybookTestResultOutput,
  type PlaybookTestSpecExpected,
} from "@repo/app-types/entities/teammateAgent";
import {
  getAccuracyClassificationForGradingValue,
  type FeedbackAccuracyData,
} from "@repo/app-types/helpers/feedbackHelpers";
import { checkPlaybookTestOutcomeMatch } from "@repo/app-types/helpers/playbookTestVerdict";

// Re-export for backwards compatibility
export { getAccuracyClassificationForGradingValue };

/**
 * Calculates accuracy metrics for a playbook test result by comparing expected values to the simulated conversation grading.
 */
export function calculatePlaybookTestResultAccuracy(
  outputGrading: PlaybookTestResultOutput,
  expectedValues: PlaybookTestSpecExpected,
  assessmentTree: PlaybookTree,
): FeedbackAccuracyData {
  let correct = 0;
  let close = 0;
  let incorrect = 0;

  const expectedNodes = expectedValues.nodes ?? {};

  // Count criteria grading matches
  if (outputGrading.criteria_grading) {
    Object.entries(outputGrading.criteria_grading).forEach(
      ([nodeId, grading]) => {
        const expectedValue = expectedNodes[nodeId];
        if (
          grading &&
          expectedValue !== undefined &&
          grading.value !== undefined
        ) {
          // Find the node to get its grading_value_type
          const node = getPlaybookNode(nodeId, assessmentTree);
          if (node?.type === "criteria") {
            const status = getAccuracyClassificationForGradingValue(
              node.grading_value_type,
              grading.value,
              String(expectedValue),
              true,
            );

            if (status === "correct") {
              correct++;
            } else if (status === "close") {
              close++;
            } else if (status === "incorrect") {
              incorrect++;
            }
          }
        }
      },
    );
  }

  // Count section grading matches
  if (outputGrading.section_grading) {
    Object.entries(outputGrading.section_grading).forEach(
      ([nodeId, grading]) => {
        const expectedValue = expectedNodes[nodeId];
        if (
          grading &&
          expectedValue !== undefined &&
          grading.is_not_applicable !== undefined
        ) {
          const status = getAccuracyClassificationForGradingValue(
            "section",
            grading.is_not_applicable ? "N/A" : "applicable",
            String(expectedValue),
            true,
          );

          if (status === "correct") {
            correct++;
          } else if (status === "close") {
            close++;
          } else if (status === "incorrect") {
            incorrect++;
          }
        }
      },
    );
  }

  const total = correct + close + incorrect;
  const accuracy = total > 0 ? (correct / total) * 100 : 0;

  return { total, correct, close, incorrect, accuracy };
}

export type TestResultStatus =
  | "passing"
  | "failing"
  | "in_progress"
  | "not_run";

/**
 * The status a view shows for one run of a test case. A completed run reads the
 * verdict the server stored; a run from before verdicts were stored falls back
 * to the outcome comparison that was the client's rule at the time.
 */
export function readPlaybookTestResultStatus(
  spec: AdminApiPlaybookTestSpec,
  result: AdminApiPlaybookTestResult | undefined,
  outcomeDefinition: OutcomeDefinition | null | undefined,
): TestResultStatus {
  if (result == null) {
    return "not_run";
  }
  if (result.status === PlaybookTestResultStatus.IN_PROGRESS) {
    return "in_progress";
  }
  if (result.status === PlaybookTestResultStatus.ERROR) {
    return "failing";
  }

  const verdict = result.output?.verdict;
  if (verdict) {
    return verdict.passed ? "passing" : "failing";
  }

  // No stored verdict: the run predates the judge.
  if (!outcomeDefinition) {
    return "passing"; // No outcome tracking, consider done = passing
  }

  return checkPlaybookTestOutcomeMatch(
    spec.expected.outcomes,
    result.output?.outcomes,
    outcomeDefinition,
  )
    ? "passing"
    : "failing";
}

export interface TestPassRateData {
  /** Number of completed runs the verdict says passed */
  passing: number;
  /** Number of completed runs */
  total: number;
  /** Total number of test specs (including those without completed results) */
  totalSpecs: number;
  /** Pass rate as a percentage (0-100) */
  percentage: number;
}

/**
 * The share of completed runs that passed, counted with the same rule the
 * per-case status uses.
 */
export function calculatePlaybookTestPassRate(
  testSpecsResults: readonly AdminApiPlaybookTestSpecResult[],
  outcomeDefinition: OutcomeDefinition | null | undefined,
  totalSpecsCount: number,
): TestPassRateData | null {
  const completedResults = testSpecsResults.filter(
    (result) =>
      result.playbook_test_result?.status === PlaybookTestResultStatus.DONE &&
      result.playbook_test_result.output,
  );

  if (completedResults.length === 0) {
    return null;
  }

  const passing = completedResults.filter(
    (result) =>
      readPlaybookTestResultStatus(
        result.playbook_test_spec,
        result.playbook_test_result,
        outcomeDefinition,
      ) === "passing",
  ).length;

  return {
    passing,
    total: completedResults.length,
    totalSpecs: totalSpecsCount,
    percentage: Math.round((passing / completedResults.length) * 100),
  };
}
